import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import axios from "axios";
import { I18N } from "../i18n";
import { PRODUCTS, FREE_SHIP_QTY } from "../data/products";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const StoreProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem("arij-lang") || "fr");
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("arij-cart")) || [];
    } catch {
      return [];
    }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [products, setProducts] = useState(PRODUCTS);
  const [quickView, setQuickView] = useState(null);

  const setLang = (l) => {
    setLangState(l);
    localStorage.setItem("arij-lang", l);
  };

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("arij-cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    let on = true;
    axios
      .get(`${API}/products`)
      .then((r) => {
        if (!on || !Array.isArray(r.data) || r.data.length === 0) return;
        setProducts(
          r.data.map((p) => {
            const st = PRODUCTS.find((s) => s.slug === p.slug) || {};
            const notes = p.notes
              ? {
                  top: { ...(p.notes.top || {}), ar: st.notes ? st.notes.top.ar : undefined },
                  heart: { ...(p.notes.heart || {}), ar: st.notes ? st.notes.heart.ar : undefined },
                  base: { ...(p.notes.base || {}), ar: st.notes ? st.notes.base.ar : undefined },
                }
              : null;
            return {
              slug: p.slug,
              nameFr: p.name_fr,
              nameEn: p.name_en,
              collection: p.collection,
              price: p.price,
              image: p.image,
              taglineFr: p.tagline_fr,
              taglineEn: p.tagline_en,
              unisex: !!p.unisex,
              featured: !!p.featured,
              notes,
              nameAr: st.nameAr || null,
              taglineAr: st.taglineAr || null,
            };
          })
        );
      })
      .catch(() => {});
    return () => {
      on = false;
    };
  }, []);

  const L = I18N[lang];

  const addToCart = (slug, qty = 1) => {
    setCart((c) => {
      const found = c.find((i) => i.slug === slug);
      if (found) return c.map((i) => (i.slug === slug ? { ...i, qty: Math.min(i.qty + qty, 99) } : i));
      return [...c, { slug, qty }];
    });
  };

  const setQty = (slug, qty) => {
    setCart((c) => (qty <= 0 ? c.filter((i) => i.slug !== slug) : c.map((i) => (i.slug === slug ? { ...i, qty } : i))));
  };

  const removeItem = (slug) => setCart((c) => c.filter((i) => i.slug !== slug));
  const clearCart = () => setCart([]);

  const count = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);
  const subtotal = useMemo(
    () =>
      Math.round(
        cart.reduce((s, i) => {
          const p = products.find((p) => p.slug === i.slug);
          return s + (p ? p.price * i.qty : 0);
        }, 0) * 100
      ) / 100,
    [cart, products]
  );

  const value = {
    lang,
    setLang,
    L,
    products,
    cart,
    addToCart,
    setQty,
    removeItem,
    clearCart,
    count,
    subtotal,
    freeShipQty: FREE_SHIP_QTY,
    cartOpen,
    setCartOpen,
    quickView,
    setQuickView,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};
